import React, { useState } from "react";
import { useT, useLanguage } from "../../lib/LanguageContext";
import Pager from "../../components/ui/Pager";
import { Loader2 } from "lucide-react";
import { PageLoadError } from "../NoAccess";
import { usePagedList } from "../usePagedList";
import { Money, DateText } from "../globalDataCells";

export const PICKER_PAGE_SIZE = 10;

/**
 * The payments a practice can be refunded for — `GET …/billing/refundable-transactions`, the
 * server's own eligibility rule (S7 contract section 5.2) — one radio each. The operator
 * picks a payment from this list; there is no way to type a reference.
 *
 * `selectedRef`: the `refund_ref` of the chosen row, `null` before a choice.
 * `onSelect(row)`: called with the whole row, so the form can show its currency and remaining
 * balance without a second read.
 */
export default function RefundTransactionPicker({ tenantId, selectedRef, onSelect }) {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const [page, setPage] = useState(1);
  const list = usePagedList({
    path: `/super-admin/tenants/${tenantId}/billing/refundable-transactions`,
    filters: {}, page, setPage, pageSize: PICKER_PAGE_SIZE,
  });

  if (list.loadFailed) return <PageLoadError error={list.loadError} onRetry={list.retry} testId="refund-picker-error" />;
  if (!list.data) return <Loader2 className="w-5 h-5 animate-spin text-amber-600" />;
  const { items, total } = list.data;
  if (total === 0) {
    return <p className="text-sm text-slate-500" data-testid="refund-none">{t("t360f.refund.none")}</p>;
  }

  return (
    <div className="mb-4" data-testid="refund-picker">
      <div className="text-xs text-slate-500 mb-2">{t("t360f.refund.pick")}</div>
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[1.5rem_5.5rem_1fr_6rem_6rem_6rem] gap-2 px-3 py-2 text-[11px] uppercase tracking-wider text-slate-400 bg-slate-50">
          <span />
          <span>{t("t360f.refund.colDate")}</span>
          <span>{t("t360f.refund.colPayment")}</span>
          <span>{t("t360f.refund.colAmount")}</span>
          <span>{t("t360f.refund.colRefunded")}</span>
          <span>{t("t360f.refund.colRemaining")}</span>
        </div>
        {items.map((row) => (
          <label
            key={row.refund_ref}
            className={`grid grid-cols-[1.5rem_5.5rem_1fr_6rem_6rem_6rem] gap-2 px-3 py-2 text-sm items-center cursor-pointer border-t border-slate-100 ${selectedRef === row.refund_ref ? "bg-amber-50" : "hover:bg-slate-50"}`}
          >
            <input
              type="radio" name="refund-transaction" checked={selectedRef === row.refund_ref}
              onChange={() => onSelect(row)} data-testid={`refund-tx-${row.refund_ref}`}
            />
            <DateText value={row.created_at} lang={lang} />
            <span className="min-w-0 break-words">
              {row.plan_name || row.description || t("t360f.refund.noDescription")}
              {row.is_one_time && <span className="ml-2 text-xs text-slate-400">{t("t360f.refund.oneTime")}</span>}
            </span>
            <Money value={row.amount} />
            <Money value={row.refunded} />
            <Money value={row.refundable} />
          </label>
        ))}
        {total > PICKER_PAGE_SIZE && (
          <Pager
            page={page} pageSize={PICKER_PAGE_SIZE} total={total} shown={items.length} onPage={setPage}
            infoText={(v) => t("sal.pagerInfo", v)} prevLabel={t("sal.pagerPrev")} nextLabel={t("sal.pagerNext")}
            testId="refund-picker-pager" tone="slate"
          />
        )}
      </div>
    </div>
  );
}
